import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, RefreshCw, SkipForward, Square } from "lucide-react";

const playBeep = () => {
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioCtx.createOscillator();
  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(1000, audioCtx.currentTime);
  oscillator.connect(audioCtx.destination);
  oscillator.start();
  oscillator.stop(audioCtx.currentTime + 0.2);
};

export default function Recorder({
  duration = 35,
  startAt = 35,
  autoStart = true,
  autoStartSignal = null,
  waitingLabel = "Waiting to record",
  canStart = true,
  onRecordingComplete,
  className = "",
}) {
  const [seconds, setSeconds] = useState(duration);
  const [countdown, setCountdown] = useState(startAt);
  const [recordingStarted, setRecordingStarted] = useState(false);
  const [recordingStopped, setRecordingStopped] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const autoStartSignalRef = useRef(autoStartSignal);

  const stopTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    stopTimer();
    if (countdownRef.current) clearInterval(countdownRef.current);
    setRecordingStarted(false);
    setRecordingStopped(true);
  }, [stopTimer]);

  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          stopRecording();
          return duration;
        }
        return prev - 1;
      });
    }, 1000);
  }, [duration, stopRecording]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/wav" });
        chunksRef.current = [];

        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
        }

        setAudioUrl(URL.createObjectURL(blob));
        onRecordingComplete?.(blob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      playBeep();
      startTimer();

      setRecordingStarted(true);
      setRecordingStopped(false);
      if (countdownRef.current) clearInterval(countdownRef.current);
    } catch (error) {
      console.error("Mic access denied:", error);
    }
  }, [audioUrl, onRecordingComplete, startTimer]);

  useEffect(() => {
    if (!autoStart) return undefined;

    if (countdown <= 0 && !recordingStarted && !recordingStopped) {
      startRecording();
      return undefined;
    }

    if (!recordingStarted && !recordingStopped) {
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(countdownRef.current);
    }

    return undefined;
  }, [autoStart, countdown, recordingStarted, recordingStopped, startRecording]);

  useEffect(() => {
    if (autoStartSignal === autoStartSignalRef.current) return;

    autoStartSignalRef.current = autoStartSignal;

    if (!recordingStarted && !recordingStopped) {
      startRecording();
    }
  }, [autoStartSignal, recordingStarted, recordingStopped, startRecording]);

  useEffect(() => {
    return () => {
      stopTimer();
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl, stopTimer]);

  const handleRetake = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    setAudioUrl(null);
    setRecordingStopped(false);
    setCountdown(startAt);
    setSeconds(duration);
  };

  const progressPercentage = (seconds / duration) * 100;
  const progressColor =
    progressPercentage <= 25
      ? "bg-red-500"
      : progressPercentage <= 50
      ? "bg-amber-400"
      : "bg-green-500";

  return (
    <div className={`${className} mx-auto rounded-md p-4`}>
      <div className="rounded-md bg-white p-4 shadow-sm ring-1 ring-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold uppercase text-purple-700">
              Recorded answer
            </p>
            <h3 className="mt-1 text-xl font-extrabold text-gray-900">
              Microphone capture
            </h3>
          </div>
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
            <Mic className="h-5 w-5" aria-hidden="true" />
          </span>
        </div>

        {!recordingStarted && !recordingStopped && (
          <div className="mt-4 rounded-md bg-purple-50 p-4 text-center ring-1 ring-purple-100">
            <p className="text-sm font-semibold uppercase text-purple-700">
              {autoStart ? "Recording starts in" : waitingLabel}
            </p>
            <p className="mt-1 text-3xl font-extrabold text-purple-900">
              {autoStart ? `${countdown}s` : "Ready"}
            </p>
          </div>
        )}

        {recordingStarted && (
          <div className="mt-4 flex items-center justify-between rounded-md bg-green-50 p-3 text-sm font-bold text-green-700 ring-1 ring-green-100">
            <span>Recording in progress</span>
            <span>{seconds}s left</span>
          </div>
        )}

        <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-slate-100">
          {recordingStarted && (
            <div
              className={`h-full rounded-full transition-all duration-500 ${progressColor}`}
              style={{ width: `${progressPercentage}%` }}
            />
          )}
        </div>

        <div className="mt-5 flex flex-wrap justify-center gap-3">
          {!recordingStarted && !recordingStopped && canStart && (
            <button
              type="button"
              onClick={startRecording}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
            >
              {autoStart ? (
                <SkipForward className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Mic className="h-4 w-4" aria-hidden="true" />
              )}
              {autoStart ? "Skip prep" : "Start recording"}
            </button>
          )}
          {recordingStarted && (
            <button
              type="button"
              onClick={stopRecording}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-red-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
            >
              <Square className="h-4 w-4" aria-hidden="true" />
              Stop
            </button>
          )}
          {recordingStopped && (
            <button
              type="button"
              onClick={handleRetake}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-white px-5 py-2.5 text-sm font-bold text-purple-700 shadow-sm ring-1 ring-purple-100 transition hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Retake
            </button>
          )}
        </div>

        {recordingStarted && (
          <div className="mt-3 text-center text-sm font-semibold text-green-600">
            Recording...
          </div>
        )}

        {audioUrl && (
          <div className="mt-5 rounded-md bg-slate-50 p-3 ring-1 ring-slate-100">
            <p className="mb-2 text-sm font-bold text-gray-700">Preview</p>
            <audio className="w-full" controls src={audioUrl}></audio>
          </div>
        )}
      </div>
    </div>
  );
}
